import type { ComponentProps } from 'react'

import NextLink from 'next/link'

/**
 * Host-provided `LinkComponent` for `DesignSystemProvider`.
 *
 * The app runs under a Next `basePath` (`/app`). Route values across the
 * app already include that prefix (e.g. `/app/overview`), and Next's
 * `<Link>` re-adds `basePath` automatically — so handing the prefixed
 * href straight to NextLink yields `/app/app/overview`.
 *
 * The pre-DS `BaseLink` stripped the basePath before delegating to
 * NextLink. DS's `BaseLink` is basePath-agnostic (it just calls the
 * injected `LinkComponent`), so that stripping now lives here, in the
 * host adapter. Uses the build-time `process.env.BASEPATH` so SSR and
 * client strip identically (no hydration mismatch).
 */
const BASEPATH = process.env.BASEPATH ?? ''

type Props = ComponentProps<typeof NextLink>

export const NextLinkAdapter = ({ href, ...props }: Props) => {
  const stripped =
    typeof href === 'string' && BASEPATH && href.startsWith(BASEPATH)
      ? href.slice(BASEPATH.length) || '/'
      : href

  return <NextLink {...props} href={stripped} legacyBehavior={false} />
}
